import React from 'react';
import { useCMS } from '../../context/CMSContext';
import { Project } from '../../types/cms';
import { getComputedTextStyle } from '../../utils/typographyHelper';
import { ArrowLinkText } from '../common/ArrowLinkText';
import { ScrollReveal } from '../common/ScrollReveal';
import { handleCtaNavigation } from '../../utils/navigationHelper';
import { OptimizedImage } from '../common/OptimizedImage';

export const ProjectsSection: React.FC = () => {
  const { data, setSelectedProject, setActivePublicPage, setIsProspectusModalOpen } = useCMS();
  const { projectsSection, typography } = data;

  const tagStyle = getComputedTextStyle(
    projectsSection?.tagStyle,
    typography?.projectsTag || typography?.sectionTags,
    { fontSize: 11, fontWeight: '500', fontColor: '#827f79', letterSpacing: '0.25em', textTransform: 'uppercase' }
  );

  const headingStyle = getComputedTextStyle(
    projectsSection?.headingStyle,
    typography?.projectsHeading || typography?.h2 || typography?.sectionHeadings,
    {
      fontSize: 52,
      fontWeight: typography?.allHeadingFontWeight || '300',
      fontColor: '#0a122a',
      lineHeight: 1.12,
      letterSpacing: '-0.02em'
    }
  );

  const subheadStyle = getComputedTextStyle(
    projectsSection?.cardLocationStyle,
    typography?.projectsParagraphs,
    typography?.bodyParagraphs || typography?.sectionParagraphs,
    { fontSize: 12, fontWeight: '300', fontColor: '#0a122a' }
  );

  const cardTitleStyle = getComputedTextStyle(
    projectsSection?.cardTitleStyle || typography?.projectCardTitle,
    typography?.h3,
    { fontSize: 13, fontWeight: '600', fontColor: '#0a122a', letterSpacing: '0.08em', textTransform: 'uppercase' }
  );

  const ctaStyle = getComputedTextStyle(
    projectsSection?.viewAllStyle,
    undefined,
    { fontSize: 12, fontWeight: '600', fontColor: '#846f55', letterSpacing: '0.25em', textTransform: 'uppercase' }
  );

  const featuredProjects = (projectsSection.projects || [])
    .filter((p) => p.isEnabled !== false)
    .slice(0, 3);

  const hasLine1 = Boolean(projectsSection.headingLine1 && projectsSection.headingLine1.trim());
  const hasLine2 = Boolean(projectsSection.headingLine2 && projectsSection.headingLine2.trim());
  const hasLine3 = Boolean(projectsSection.headingLine3 && projectsSection.headingLine3.trim());
  const hasLine4 = Boolean(projectsSection.headingLine4 && projectsSection.headingLine4.trim());

  const headingLines: string[] = (hasLine1 || hasLine2 || hasLine3 || hasLine4)
    ? [
        projectsSection.headingLine1,
        projectsSection.headingLine2,
        projectsSection.headingLine3,
        projectsSection.headingLine4
      ].filter(Boolean) as string[]
    : (projectsSection.heading || 'Land chosen before it was obvious').split('\n');

  const handleCardClick = (project: Project) => {
    setSelectedProject(project);
    setActivePublicPage('project-detail');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewAllClick = () => {
    handleCtaNavigation(
      projectsSection.viewAllTargetPage || 'projects',
      projectsSection.viewAllUrl,
      {
        setActivePublicPage,
        openProspectusModal: () => setIsProspectusModalOpen(true),
        defaultTarget: 'projects'
      }
    );
  };

  return (
    <section id="projects" className="py-24 md:py-32 bg-[#f7f2e8] text-[#1c1d20]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        
        {/* 4-Column Layout matching screenshot: 1 column for Title & CTA, 3 columns for Project Cards */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-8 items-start">
          
          {/* Left Title Column */}
          <div className="md:col-span-4 lg:col-span-3 space-y-6 md:pr-4">
            <ScrollReveal delay={0}>
              <div className="flex items-center space-x-3" style={tagStyle}>
                <span>{projectsSection.tag || 'FEATURED PROJECTS'}</span>
                <span className="w-12 h-[1px] bg-current opacity-60 inline-block"></span>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={120}>
              <h2
                className="font-jost text-4xl sm:text-5xl lg:text-[52px] tracking-tight leading-[1.12]"
                style={headingStyle}
              >
                {headingLines.map((line, lIdx) => (
                  <div key={lIdx}>{line}</div>
                ))}
              </h2>
            </ScrollReveal>

            <ScrollReveal delay={240}>
              <div className="pt-2">
                <button
                  onClick={handleViewAllClick}
                  style={ctaStyle}
                  className="text-xs tracking-[0.25em] text-[#846f55] uppercase font-semibold flex items-center group cursor-pointer font-sans hover:text-[#a88345] transition-colors"
                >
                  <ArrowLinkText text={projectsSection.viewAllText || 'VIEW ALL PROJECTS  —→'} defaultText="VIEW ALL PROJECTS" />
                </button>
              </div>
            </ScrollReveal>
          </div>

          {/* Right 3 Project Cards matching screenshot */}
          <div className="md:col-span-8 lg:col-span-9 grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-6">
            {featuredProjects.map((project, idx) => (
              <ScrollReveal key={project.id} delay={150 + idx * 100}>
                <div
                  onClick={() => handleCardClick(project)}
                  className="group cursor-pointer flex flex-col space-y-3 transition-transform duration-300 hover:-translate-y-1"
                >
                  {/* Image Container */}
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-[#1f2125] flex items-center justify-center">
                    {project.image && project.image.trim() !== '' ? (
                      <OptimizedImage
                        src={project.image}
                        alt={project.title}
                        width={400}
                        height={300}
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 33vw, 400px"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
                        pictureClassName="w-full h-full block"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center bg-[#181a1f] text-white/40">
                        <span className="text-[11px] font-mono uppercase tracking-widest text-[#a88345]">Oltraya Estate</span>
                        <span className="text-[10px] text-white/30 mt-1">{project.title}</span>
                      </div>
                    )}
                  </div>

                  {/* Text Info matching screenshot */}
                  <div className="space-y-0.5">
                    <h3
                      className="font-sans group-hover:text-[#846f55] transition-colors"
                      style={cardTitleStyle}
                    >
                      {project.title}
                    </h3>
                    <p className="text-xs font-light font-jost" style={subheadStyle}>
                      {project.location}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

        </div>

      </div>
    </section>
  );
};
